import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Panel } from 'primereact/panel';
import { formatarCPF, formatarCNPJ } from "../../../utils";
import { AutenticaCadastralProps } from "../../../interface/AuthScore";

// ─── Score Gauge SVG Component ──────────────────────────────────────────────────
function ScoreGaugeSVG({ score, color }: { score: number; color: string }) {
  const radius = 70;
  const strokeWidth = 14;
  const circumference = Math.PI * radius; // semicircle
  const percentage = Math.max(0, Math.min(1000, score)) / 1000;
  const strokeDashoffset = circumference - percentage * circumference;

  // Pointer angle on the arc: -180deg (left) → 0deg (right)
  const angle = -180 + percentage * 180;
  const rad = (angle * Math.PI) / 180;
  const cx = 100;
  const cy = 96;
  const px = cx + radius * Math.cos(rad);
  const py = cy + radius * Math.sin(rad);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width="200" height="110" viewBox="0 0 200 110" style={{ overflow: 'visible' }}>
        {/* Track */}
        <path
          d="M 30,96 A 70,70 0 0,1 170,96"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d="M 30,96 A 70,70 0 0,1 170,96"
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s ease-in-out' }}
        />
        {/* Pointer dot */}
        <circle cx={px} cy={py} r={7} fill={color} stroke="#FFF" strokeWidth={2} />
        {/* Score text */}
        <text x="100" y="88" textAnchor="middle" fontSize="28" fontWeight="800" fill={color}>
          {score}
        </text>
        <text x="100" y="104" textAnchor="middle" fontSize="10" fill="#9CA3AF" fontWeight="600">
          de 1000
        </text>
      </svg>
    </div>
  );
}

// ─── Risk Bar Component ────────────────────────────────────────────────────────
function RiskBar({ scoreValue }: { scoreValue: number }) {
  const position = Math.min(100, Math.max(0, ((scoreValue - 300) / 700) * 100));

  const ranges = [
    { label: 'Muito Alto', range: '300 a 500', color: '#DE1616' },
    { label: 'Alto', range: '500 a 600', color: '#FF9700' },
    { label: 'Médio', range: '600 a 700', color: '#FFCA39' },
    { label: 'Baixo', range: '700 a 900', color: '#0DAB01' },
    { label: 'Muito Baixo', range: '900 a 1000', color: '#1FA685' },
  ];

  return (
    <div>
      <p style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#374151', margin: '0 0 8px 0' }}>
        Entenda a distribuição da faixa de risco
      </p>
      {/* Gradient bar */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '10px',
          borderRadius: '5px',
          background:
            'linear-gradient(to right, #DE1616 0%, #DE1616 28.5%, #FF9700 28.5%, #FF9700 42.8%, #FFCA39 42.8%, #FFCA39 57.1%, #0DAB01 57.1%, #0DAB01 85.6%, #1FA685 85.6%, #1FA685 100%)',
          marginBottom: '14px',
          marginTop: '8px',
        }}
      >
        {/* Marker */}
        <div
          style={{
            position: 'absolute',
            left: `${position}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '6px',
            height: '18px',
            borderRadius: '3px',
            backgroundColor: '#1F2937',
            border: '1px solid #111827',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            transition: 'left 0.8s ease-in-out',
          }}
        />
      </div>

      {/* Labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
        {ranges.map(r => (
          <div key={r.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: r.color,
                display: 'inline-block',
                marginBottom: '2px',
              }}
            />
            <span style={{ fontSize: '0.6rem', color: '#6B7280', fontWeight: 600, textAlign: 'center' }}>
              {r.label}
            </span>
            <span style={{ fontSize: '0.55rem', color: '#9CA3AF', textAlign: 'center' }}>{r.range}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const getScoreColor = (scoreValue: number) => {
  if (scoreValue <= 300) return '#DE1616';
  if (scoreValue <= 500) return '#FF9700';
  if (scoreValue <= 700) return '#FFCA39';
  if (scoreValue <= 900) return '#0DAB01';
  return '#1FA685';
};

const getScoreRiscoConfig = (scoreValue: number) => {
  if (scoreValue <= 300) {
    return { color: '#DE1616', label: 'Muito Alto', bg: '#FDE8E8', text: '#9B1C1C', border: '#F8B4B4' };
  }
  if (scoreValue <= 500) {
    return { color: '#FF9700', label: 'Alto', bg: '#FFF3E0', text: '#B45309', border: '#FCD34D' };
  }
  if (scoreValue <= 700) {
    return { color: '#FFCA39', label: 'Médio', bg: '#FEFCE8', text: '#854D0E', border: '#FEF08A' };
  }
  if (scoreValue <= 900) {
    return { color: '#0DAB01', label: 'Baixo', bg: '#DEF7EC', text: '#03543F', border: '#84E1BC' };
  }
  return { color: '#1FA685', label: 'Muito Baixo', bg: '#ECFDF5', text: '#065F46', border: '#6EE7B7' };
};

const formatCEP = (cepStr: string) => {
  if (!cepStr) return '-';
  const clean = cepStr.replace(/\D/g, "");
  if (clean.length !== 8) return cepStr;
  return `${clean.slice(0, 5)}-${clean.slice(5)}`;
};

const formatTelefone = (telStr: string) => {
  if (!telStr) return '';
  const digits = telStr.replace(/\D/g, "");
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 8 || digits.length === 9) {
    return `${digits.slice(0, digits.length - 4)}-${digits.slice(digits.length - 4)}`;
  }
  return telStr;
};

// ─── Reusable Field Component ───────────────────────────────────────────────────
function CadastralField({ label, value }: { label: string; value: any }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontSize: 'small', color: '#1F2937', fontWeight: 500, wordBreak: 'break-word' }}>
        {value !== undefined && value !== null && value !== '' ? String(value) : '—'}
      </span>
    </div>
  );
}

// ─── Status Badge Component ──────────────────────────────────────────────────────
function StatusBadge({
  label,
  value,
  type,
}: {
  label: string;
  value: any;
  type: 'boolean' | 'deceased' | 'inactive' | 'confirmation' | 'carriers' | 'reason';
}) {
  if (value === undefined || value === null || value === '') {
    return <CadastralField label={label} value="—" />;
  }

  let textValue = String(value);
  let color = '#374151';
  let bg = '#F3F4F6';
  let border = '#E5E7EB';
  let icon = '';

  if (type === 'boolean' || type === 'deceased' || type === 'inactive') {
    const isYes =
      String(value).toUpperCase() === 'SIM' ||
      String(value).toUpperCase() === 'S' ||
      value === true ||
      String(value) === '1';
    const isNo =
      String(value).toUpperCase() === 'NÃO' ||
      String(value).toUpperCase() === 'NAO' ||
      String(value).toUpperCase() === 'N' ||
      value === false ||
      String(value) === '0';

    if (type === 'deceased') {
      if (isYes) {
        textValue = 'Óbito Confirmado';
        color = '#DC2626';
        bg = '#FEE2E2';
        border = '#FCA5A5';
        icon = 'pi pi-exclamation-triangle';
      } else if (isNo) {
        textValue = 'Regular';
        color = '#059669';
        bg = '#D1FAE5';
        border = '#A7F3D0';
        icon = 'pi pi-check';
      }
    } else if (type === 'inactive') {
      if (isYes) {
        textValue = 'Inativo';
        color = '#DC2626';
        bg = '#FEE2E2';
        border = '#FCA5A5';
        icon = 'pi pi-times-circle';
      } else if (isNo) {
        textValue = 'Ativo';
        color = '#059669';
        bg = '#D1FAE5';
        border = '#A7F3D0';
        icon = 'pi pi-check-circle';
      }
    } else {
      if (isYes) {
        textValue = 'Sim';
        color = '#059669';
        bg = '#D1FAE5';
        border = '#A7F3D0';
        icon = 'pi pi-check';
      } else if (isNo) {
        textValue = 'Não';
        color = '#374151';
        bg = '#F3F4F6';
        border = '#E5E7EB';
        icon = 'pi pi-times';
      }
    }
  } else if (type === 'confirmation') {
    const status = String(value).toUpperCase();
    if (status === 'FORTE') {
      textValue = 'Forte';
      color = '#059669';
      bg = '#D1FAE5';
      border = '#A7F3D0';
      icon = 'pi pi-shield';
    } else if (status === 'MÉDIO' || status === 'MEDIO') {
      textValue = 'Médio';
      color = '#D97706';
      bg = '#FEF3C7';
      border = '#FDE68A';
      icon = 'pi pi-info-circle';
    } else if (status === 'FRACO') {
      textValue = 'Fraco';
      color = '#DC2626';
      bg = '#FEE2E2';
      border = '#FCA5A5';
      icon = 'pi pi-exclamation-triangle';
    }
  } else if (type === 'carriers') {
    const status = String(value);
    if (status === '1') {
      textValue = 'Todas responderam';
      color = '#059669';
      bg = '#D1FAE5';
      border = '#A7F3D0';
      icon = 'pi pi-check';
    } else if (status === '0') {
      textValue = 'Alguma não respondeu';
      color = '#D97706';
      bg = '#FEF3C7';
      border = '#FDE68A';
      icon = 'pi pi-exclamation-circle';
    }
  } else if (type === 'reason') {
    const status = String(value);
    if (status === '1') {
      textValue = 'Confirmado';
      color = '#059669';
      bg = '#D1FAE5';
      border = '#A7F3D0';
      icon = 'pi pi-check-circle';
    } else if (status === '2') {
      textValue = 'Tel não cadastrado';
      color = '#DC2626';
      bg = '#FEE2E2';
      border = '#FCA5A5';
      icon = 'pi pi-exclamation-triangle';
    } else if (status === '0') {
      textValue = 'Não encontrado';
      color = '#D97706';
      bg = '#FEF3C7';
      border = '#FDE8E8';
      icon = 'pi pi-question-circle';
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: 'small',
            fontWeight: 600,
            color,
            backgroundColor: bg,
            border: `1px solid ${border}`,
            padding: '4px 10px',
            borderRadius: '6px',
          }}
        >
          {icon && <i className={icon} style={{ fontSize: '10px' }}></i>}
          {textValue}
        </span>
      </div>
    </div>
  );
}

// ─── Sub-section header ──────────────────────────────────────────────────────────
function SectionHeader({ icon, label }: { icon: string; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: '24px 0 12px',
        paddingBottom: '8px',
        borderBottom: '1px solid #E5E7EB',
      }}
    >
      <i className={icon} style={{ color: '#2563EB', fontSize: '14px' }} />
      <span style={{ fontWeight: 700, color: '#1E3A8A', fontSize: 'small' }}>{label}</span>
    </div>
  );
}

export default function AutenticaCadastral({ quodAutenticaCadastral }: AutenticaCadastralProps) {
  const { t } = useTranslation();

  if (!quodAutenticaCadastral) return null;

  let header: any = {};
  let outputs: any[] = [];
  let exceptions: any[] = [];

  if (Array.isArray(quodAutenticaCadastral)) {
    outputs = quodAutenticaCadastral;
  } else {
    const root =
      quodAutenticaCadastral?.AuthScoreResponseEx?.response ||
      quodAutenticaCadastral?.AuthScoreResponse?.response ||
      quodAutenticaCadastral?.AuthScoreResellerResponse?.response ||
      quodAutenticaCadastral?.response ||
      quodAutenticaCadastral;

    header = root?.Header || root?.header || {};
    const records = root?.Records || root?.records || {};

    const rawOutput = records?.AuthScoreOutput || records?.authScoreOutput;
    if (Array.isArray(rawOutput)) {
      outputs = rawOutput;
    } else if (rawOutput && typeof rawOutput === 'object') {
      outputs = [rawOutput];
    }

    exceptions = (() => {
      const ex =
        header?.Exceptions?.Exception ??
        header?.exceptions?.exception ??
        quodAutenticaCadastral?.Exceptions?.Exception ??
        [];
      if (Array.isArray(ex)) return ex;
      if (ex && typeof ex === 'object') return [ex];
      return [];
    })();
  }

  // Fallback to direct object if outputs is empty but quodAutenticaCadastral has a score
  if (outputs.length === 0) {
    const possibleScore =
      quodAutenticaCadastral?.Score ??
      quodAutenticaCadastral?.score ??
      quodAutenticaCadastral?.AuthScoreOutput?.Score ??
      quodAutenticaCadastral?.AuthScoreOutput?.score;
    if (possibleScore !== undefined && possibleScore !== null) {
      outputs = [quodAutenticaCadastral];
    }
  }

  const hasError = exceptions.length > 0;

  return (
    <Panel header="Autêntica Cadastral">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Errors Block */}
        {hasError && (
          <div
            style={{
              backgroundColor: '#FEE2E2',
              border: '1px solid #FCA5A5',
              borderRadius: '12px',
              padding: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <i className="pi pi-exclamation-circle" style={{ color: '#DC2626', fontSize: '16px' }} />
              <span style={{ fontWeight: 700, color: '#991B1B', fontSize: '0.9rem' }}>Erros na Consulta</span>
            </div>
            {exceptions.map((ex: any, i: number) => (
              <div key={i} style={{ fontSize: '0.85rem', color: '#7F1D1D', marginBottom: '4px' }}>
                <strong>Código {ex.Code ?? ex.code}:</strong> {ex.Message ?? ex.message}
              </div>
            ))}
          </div>
        )}

        {/* Records Output List */}
        {outputs.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {outputs.map((item, index) => {
              const scoreVal = item.Score ?? item.score;
              const scoreValue = scoreVal !== undefined && scoreVal !== null ? Number(scoreVal) : 0;
              const riscoConfig = getScoreRiscoConfig(scoreValue);
              const model = item.ModelCode ?? item.modelCode ?? '';
              const returnCode = item.ReturnCode ?? item.returnCode ?? '';
              const flagCod = item.FlagCod ?? item.flagCod ?? '';

              const cpf = item.CPF ?? item.cpf ?? '';
              const phone = item.Phone ?? item.phone ?? '';
              const cep = item.CEP ?? item.cep ?? '';
              const resNumber = item.ResidenceNumber ?? item.residenceNumber ?? '';

              // Boolean / Status checks
              const isDeceased = item.IsDeceased ?? item.isDeceased;
              const isPeP = item.IsPeP ?? item.isPeP;
              const isVip = item.IsVip ?? item.isVip;
              const isInactive = item.IsInactive ?? item.isInactive;
              const isMinor = item.IsMinor ?? item.isMinor;

              const isConfirmedPhone = item.IsConfirmedPhone ?? item.isConfirmedPhone;
              const isConfirmedEmail = item.IsConfirmedEmail ?? item.isConfirmedEmail;
              const addressConfirmationStatus =
                item.AdressConfirmationStatus ??
                item.adressConfirmationStatus ??
                item.AddressConfirmationStatus ??
                item.addressConfirmationStatus;
              const allCarriersResponded = item.AllCarriersResponded ?? item.allCarriersResponded;
              const reasonCode = item.ReasonCode ?? item.reasonCode;

              // Quod alternative details
              const phoneQuod = item.PhoneQuod ?? item.phoneQuod;
              const emailQuod = item.EmailQuod ?? item.emailQuod;
              const addressQuod = item.AddressQuod ?? item.addressQuod;

              const hasQuodData = phoneQuod || emailQuod || addressQuod;

              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                  }}
                >
                  {/* Card Title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #F3F4F6', paddingBottom: '12px' }}>
                    <div
                      style={{
                        backgroundColor: '#EFF6FF',
                        borderRadius: '8px',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="pi pi-user" style={{ color: '#2563EB', fontSize: '16px' }} />
                    </div>
                    <span style={{ fontWeight: 700, color: '#1E3A8A', fontSize: '0.95rem' }}>
                      Registro de Autenticação {outputs.length > 1 ? `#${index + 1}` : ''}
                    </span>

                  </div>

                  {/* Score Section */}
                  {scoreValue > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', backgroundColor: '#F9FAFB', borderRadius: '12px', padding: '20px', border: '1px solid #E5E7EB' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
                        <div style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center' }}>
                          <ScoreGaugeSVG score={scoreValue} color={riscoConfig.color} />
                        </div>
                        <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div
                            style={{
                              backgroundColor: riscoConfig.bg,
                              border: `1px solid ${riscoConfig.border}`,
                              borderRadius: '12px',
                              padding: '14px 16px',
                            }}
                          >
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                              <i className="pi pi-exclamation-triangle" style={{ color: riscoConfig.color, fontSize: '14px' }}></i>
                              <div>
                                <span style={{ fontWeight: 700, color: riscoConfig.text, fontSize: '0.85rem' }}>
                                  Probabilidade de Inconsistência Cadastral: Risco {riscoConfig.label}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <RiskBar scoreValue={scoreValue} />
                    </div>
                  )}

                  {/* Primary Data Grid */}
                  {(cpf || phone || cep || resNumber) && (
                    <div>
                      <SectionHeader icon="pi pi-info-circle" label="Dados Cadastrais Informados" />
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                          gap: '16px',
                        }}
                      >
                        {cpf && <CadastralField label="CPF" value={formatarCPF(cpf)} />}
                        {phone && <CadastralField label="Telefone" value={formatTelefone(phone)} />}
                        {cep && <CadastralField label="CEP" value={formatCEP(cep)} />}
                        {resNumber && <CadastralField label="Número Residencial" value={resNumber} />}
                      </div>
                    </div>
                  )}

                  {/* Validation Indicators Grid */}
                  {(isDeceased !== undefined ||
                    isInactive !== undefined ||
                    isPeP !== undefined ||
                    isVip !== undefined ||
                    isMinor !== undefined ||
                    isConfirmedPhone !== undefined ||
                    isConfirmedEmail !== undefined ||
                    addressConfirmationStatus !== undefined ||
                    allCarriersResponded !== undefined ||
                    reasonCode !== undefined) && (
                      <div>
                        <SectionHeader icon="pi pi-shield" label="Validações Cadastrais" />
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px',
                          }}
                        >
                          {isDeceased !== undefined && (
                            <StatusBadge label="Indicador de Óbito" value={isDeceased} type="deceased" />
                          )}
                          {isInactive !== undefined && (
                            <StatusBadge label="Indicador de Inatividade" value={isInactive} type="inactive" />
                          )}
                          {isPeP !== undefined && (
                            <StatusBadge label="Politicamente Exposto (PEP)" value={isPeP} type="boolean" />
                          )}
                          {isVip !== undefined && (
                            <StatusBadge label="Pessoa VIP" value={isVip} type="boolean" />
                          )}
                          {isMinor !== undefined && (
                            <StatusBadge label="Menor de Idade" value={isMinor} type="boolean" />
                          )}
                          {isConfirmedPhone !== undefined && (
                            <StatusBadge label="Telefone Confirmado" value={isConfirmedPhone} type="boolean" />
                          )}
                          {isConfirmedEmail !== undefined && (
                            <StatusBadge label="E-mail Confirmado" value={isConfirmedEmail} type="boolean" />
                          )}
                          {addressConfirmationStatus !== undefined && (
                            <StatusBadge
                              label="Status Confirmação de Endereço"
                              value={addressConfirmationStatus}
                              type="confirmation"
                            />
                          )}
                          {allCarriersResponded !== undefined && (
                            <StatusBadge
                              label="Retorno de Operadoras"
                              value={allCarriersResponded}
                              type="carriers"
                            />
                          )}
                          {reasonCode !== undefined && (
                            <StatusBadge
                              label="Confirmação Chave CPF/Telefone"
                              value={reasonCode}
                              type="reason"
                            />
                          )}
                        </div>
                      </div>
                    )}

                  {/* Alternative Data (Quod) */}
                  {hasQuodData && (
                    <div>
                      <SectionHeader icon="pi pi-database" label="Dados Alternativos Encontrados (Quod)" />
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                          gap: '16px',
                        }}
                      >
                        {phoneQuod && <CadastralField label="Telefone Alternativo (Quod)" value={formatTelefone(phoneQuod)} />}
                        {emailQuod && <CadastralField label="E-mail Alternativo (Quod)" value={emailQuod} />}
                        {addressQuod && <CadastralField label="Endereço Alternativo (Quod)" value={addressQuod} />}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Raw fallback if no records found */
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              padding: '24px',
              textAlign: 'center',
            }}
          >
            <i className="pi pi-database" style={{ fontSize: '2rem', color: '#9CA3AF', marginBottom: '12px' }} />
            <p style={{ margin: 0, color: '#4B5563', fontSize: 'small' }}>Nenhum detalhe de autenticação cadastral retornado.</p>
          </div>
        )}
      </div>
    </Panel>
  );
}
